import * as React from "react";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, Typography } from '@material-ui/core';
import Paper from '@mui/material/Paper';
import {
  ArgumentAxis,
  ValueAxis,
  Chart,
  LineSeries,
} from '@devexpress/dx-react-chart-material-ui';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { PageListTitle } from "./components/pageTitles";
import {Datagrid, List, Pagination, ReferenceField, TextField, useDataProvider} from "react-admin";
import { fetcherGetSync } from "./utils/fetcher";
import config, { ORDER_STATUS_ID_REF } from "./config";
import moment from "moment";
import { roundPrice } from "./utils/utils";
import { Stack } from "@mui/material";
import { DeliveredDateComponent, OrderDateComponent, PickupDateComponent, PriceComponent, UserLinkComponent } from "./pages/orders";


function getCurrentYearAndMonth(){
    return moment().format("YYYY-MM");
}

function getTotalPriceOfOrder(order){
    let totalPrice = 0;
    for(let product of order.products){
        totalPrice = totalPrice + product.price * product.count;
    }
    totalPrice += order.deliveryPrice;
    return totalPrice;
}

// day: ex 2022-09-23
function getRevenueOfDay(orders, day){
    let revenue = 0;
    for(let order of orders){
        if(moment(order.creationDate, "X").isSame(moment(day, "YYYY-MM-DD"), "day")){
            revenue += getTotalPriceOfOrder(order);
        }
    }

    return revenue;
}

function daysInMonth (month, year) {
    return new Date(year, month, 0).getDate();
}

function generateDatesInMonth(month, year){
    const nbOfDays = daysInMonth(month, year);
    var days = [];
    for (let i = 1; i <= nbOfDays; i++) { 
        days.push(i);
    }
    return days;
}

function generateOrdersDataForChart(orders){
    const data = [];

    const currentYearAndMonth = getCurrentYearAndMonth();
    for (const day of generateDatesInMonth(currentYearAndMonth.split("-")[1], currentYearAndMonth.split("-")[0])) {
        
        let val = getRevenueOfDay(orders, currentYearAndMonth+"-"+day);
        data.push({argument: day, value: val});
    }

    return data;
}

function getTotalRevenueOfCurrentMonth(orders){
    let revenue = 0;
    for(let order of orders){
        if(moment(order.creationDate, "X").isSame(moment(), "month")){
            revenue += getTotalPriceOfOrder(order);
        }
    }

    return revenue;
}

function getTotalRevenue(orders) {
    let revenue = 0;
    for(let order of orders){
        revenue += getTotalPriceOfOrder(order);
    }

    return revenue;
}


export default () => {
    const dataProvider = useDataProvider();
    const [chartData, setChartData] = useState([]);
    const [orders, setOrders] = useState([]);
    const [ordersCancelled, setOrdersCancelled] = useState([]);
    const [individuals, setIndividuals] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [sibAccount, setSibAccount] = useState([]);
    const [totalRevenueOfMonth, setTotalRevenueOfMonth] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);

    const loadOrders = () => {
        let orders = fetcherGetSync(config.API_HOST+"/orders/complete");
        orders = orders.filter(e => e.statusId != config.ORDER_STATUS_REF_ID["CANCELLED"]);
        return orders;
    }

    const loadOrdersCancelled = () => {
        let orders = fetcherGetSync(config.API_HOST+"/orders/complete");
        orders = orders.filter(e => e.statusId == config.ORDER_STATUS_REF_ID["CANCELLED"]);
        return orders;
    }

    const loadIndividuals = () => {
        let individuals = fetcherGetSync(config.API_HOST+"/users/individual");
        return individuals;
    }

    const loadBusinesses = () => {
        let businesses = fetcherGetSync(config.API_HOST+"/users/business");
        return businesses;
    }

    const loadSIBAccount = () => {
        let sibAccount = fetcherGetSync(config.API_HOST+"/utils/getSIBAccount");
        return sibAccount;
    }

    const roundPrice = (price) => {
        return Math.round(price*100)/100;
    }

    useEffect(() => {
        let orders = loadOrders();
        let ordersCancelled = loadOrdersCancelled();
        let individuals = loadIndividuals();
        let businesses = loadBusinesses();
        let sibAccount = loadSIBAccount();
        setChartData(generateOrdersDataForChart(orders));
        setOrders(orders);
        setOrdersCancelled(ordersCancelled)
        setIndividuals(individuals);
        setBusinesses(businesses);
        setTotalRevenueOfMonth(roundPrice(getTotalRevenueOfCurrentMonth(orders)))
        setTotalRevenue(roundPrice(getTotalRevenue(orders)));
        setSibAccount(sibAccount);
    }, [])

    return (
        <Card>
            <PageListTitle text="Panel d'administration" />
            <CardContent>

                <Typography style={{textAlign: "center", marginTop: "10px", marginBottom: "20px"}} variant="h5" component="h2">
                    Progression du chiffre d'affaires journalier
                </Typography>

                <Typography style={{textAlign: "center", marginTop: "10px", marginBottom: "20px"}} variant="h6" component="h3">
                    Total de {getCurrentYearAndMonth().split("-")[1]}/{getCurrentYearAndMonth().split("-")[0]} : {totalRevenueOfMonth} € TTC
                </Typography>

                <Paper>
                    <Chart
                    data={chartData}
                    >
                        <ArgumentAxis />
                        <ValueAxis />

                        <LineSeries valueField="value" argumentField="argument" />
                    </Chart>
                </Paper>

                <Stack direction="row" spacing={2} style={{marginTop: 30}}>
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Informations du site</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Client particuliers
                                    </TableCell>
                                    <TableCell>{individuals.length}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Client professionnels
                                    </TableCell>
                                    <TableCell>{businesses.length}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Commandes (dont annulées)
                                    </TableCell>
                                    <TableCell>{orders.filter(order => !(["COMMANDING"].includes(ORDER_STATUS_ID_REF[order.statusId]))).length + ordersCancelled.length}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Commandes en traitement
                                    </TableCell>
                                    <TableCell>{orders.filter(order => (["PAID", "TREATED"].includes(ORDER_STATUS_ID_REF[order.statusId]))).length}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Commandes annulées
                                    </TableCell>
                                    <TableCell>{ordersCancelled.length}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Statistiques de vente</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Chiffre d'affaires TTC
                                    </TableCell>
                                    <TableCell>{totalRevenue} €</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Chiffre d'affaires TTC du mois en cours
                                    </TableCell>
                                    <TableCell>{totalRevenueOfMonth} €</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        Panier moyen TTC
                                    </TableCell>
                                    <TableCell>{roundPrice(totalRevenue / orders.length)} €</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Crédits SendInBlue</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    sibAccount && sibAccount.plan ?
                                        sibAccount.plan.map(plan => {
                                            return (<TableRow>
                                                <TableCell component="th" scope="row">
                                                    {plan.type}
                                                </TableCell>
                                                <TableCell>{plan.credits} crédits</TableCell>
                                            </TableRow>)
                                        })
                                    :
                                    <></>
                                }
                                
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>

                <Typography style={{textAlign: "center", marginTop: "50px", marginBottom: "0px"}} variant="h6" component="h3">
                    Nouvelles commandes
                </Typography>

                <List exporter={false} empty={<span style={{margin: "auto"}}>Aucune nouvelle commande</span>} pagination={<Pagination rowsPerPageOptions={[99, 100]} />} perPage={100} filter={{ statusId: [config.ORDER_STATUS_REF_ID["STARTED"]] }} resource="orders" basePath="">
                    <Datagrid rowClick="edit">
                        <TextField source="id" label="ID" />
                        <OrderDateComponent label="Date" />
                        <UserLinkComponent label="Client" />
                        <ReferenceField link={false} source="statusId" reference="orderStatuses">
                            <TextField source="title" label="Status" />
                        </ReferenceField>
                        <PickupDateComponent label="Date d'enlèvement" />
                        <DeliveredDateComponent label="Date de retour" />
                        {/*<FastDeliveryComponent label="Livraison rapide" />*/}
                        <PriceComponent label="Prix (TTC)" />
                    </Datagrid>
                </List>

                <Typography style={{textAlign: "center", marginTop: "50px", marginBottom: "0px"}} variant="h6" component="h3">
                    Commandes en enlèvement
                </Typography>

                <List exporter={false} empty={<span style={{margin: "auto"}}>Aucune commande en enlèvement</span>} pagination={<Pagination rowsPerPageOptions={[99, 100]} />} perPage={100} filter={{ statusId: [config.ORDER_STATUS_REF_ID["RECEIVING"], config.ORDER_STATUS_REF_ID["RECEIVED"]] }} resource="orders" basePath="">
                    <Datagrid rowClick="edit">
                        <TextField source="id" label="ID" />
                        <OrderDateComponent label="Date" />
                        <UserLinkComponent label="Client" />
                        <ReferenceField link={false} source="statusId" reference="orderStatuses">
                            <TextField source="title" label="Status" />
                        </ReferenceField>
                        <PickupDateComponent label="Date d'enlèvement" />
                        <DeliveredDateComponent label="Date de retour" />
                        {/*<FastDeliveryComponent label="Livraison rapide" />*/}
                        <PriceComponent label="Prix (TTC)" />
                    </Datagrid>
                </List>

                <Typography style={{textAlign: "center", marginTop: "50px", marginBottom: "0px"}} variant="h6" component="h3">
                    Commandes en traitement
                </Typography>

                <List exporter={false} empty={<span style={{margin: "auto"}}>Aucune commande en enlèvement</span>} pagination={<Pagination rowsPerPageOptions={[99, 100]} />} perPage={100} filter={{ statusId: [config.ORDER_STATUS_REF_ID["PAID"], config.ORDER_STATUS_REF_ID["TREATED"]] }} resource="orders" basePath="">
                    <Datagrid rowClick="edit">
                        <TextField source="id" label="ID" />
                        <OrderDateComponent label="Date" />
                        <UserLinkComponent label="Client" />
                        <ReferenceField link={false} source="statusId" reference="orderStatuses">
                            <TextField source="title" label="Status" />
                        </ReferenceField>
                        <PickupDateComponent label="Date d'enlèvement" />
                        <DeliveredDateComponent label="Date de retour" />
                        {/*<FastDeliveryComponent label="Livraison rapide" />*/}
                        <PriceComponent label="Prix (TTC)" />
                    </Datagrid>
                </List>

                <Typography style={{textAlign: "center", marginTop: "50px", marginBottom: "0px"}} variant="h6" component="h3">
                    Commandes en retour
                </Typography>

                <List exporter={false} empty={<span style={{margin: "auto"}}>Aucune commande en retour</span>} pagination={<Pagination rowsPerPageOptions={[99, 100]} />} perPage={99} filter={{ statusId: [config.ORDER_STATUS_REF_ID["RETURNING"]] }} resource="orders" basePath="">
                    <Datagrid rowClick="edit">
                        <TextField source="id" label="ID" />
                        <OrderDateComponent label="Date" />
                        <UserLinkComponent label="Client" />
                        <ReferenceField link={false} source="statusId" reference="orderStatuses">
                            <TextField source="title" label="Status" />
                        </ReferenceField>
                        <PickupDateComponent label="Date d'enlèvement" />
                        <DeliveredDateComponent label="Date de retour" />
                        {/*<FastDeliveryComponent label="Livraison rapide" />*/}
                        <PriceComponent label="Prix (TTC)" />
                    </Datagrid>
                </List>

            </CardContent>
        </Card>
    )
};